import { type ReactNode, memo, useEffect, useMemo, useState } from 'react';

import {
  ACTOR_ACCENT_BG_CLASSES,
  usePlayerActions,
  usePlayerAssetStatus,
  usePlayerSettings,
  usePlayerStore,
  usePlayerStoreApi,
  usePlayerTimeline,
} from '@cartwhl/3d/player';
import formatTime from '@cartwhl/common/utils/formatTime';
import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import LightweightSlider from '@cartwhl/ui/atoms/LightweightSlider';
import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import Text from '@cartwhl/ui/base/Text';
import TextNew from '@cartwhl/ui/base/TextNew';
import ChevronDownIcon from '@cartwhl/ui/icons/ChevronDownIcon';
import ChevronLeftIcon from '@cartwhl/ui/icons/ChevronLeftIcon';
import PauseIcon from '@cartwhl/ui/icons/PauseIcon';
import PlayIcon from '@cartwhl/ui/icons/PlayIcon';
import Button from '@cartwhl/ui/input/Button';
import CheckboxButton from '@cartwhl/ui/input/CheckboxButton';
import GoButton from '@cartwhl/ui/input/GoButton';
import KeyPoseTimeline from '@cartwhl/ui/player/KeyPoseTimeline';

import {
  ENABLE_MOTION_EDITOR_KEY_POSE_SURFACES,
  useDebugStampEveryEnabled,
} from '../feature-flags';
import { useKeyPoseCaptureActions } from '../keyPoses/hooks/useKeyPoseCaptureActions';
import {
  useStore as useKeyPoseStore,
  useStoreApi as useKeyPoseStoreApi,
} from '../keyPoses/keyPoseStore';
import { useStore as useMotionEditorStore } from '../motionEditorStore';
import type { MotionEditorActor } from '../types';
import ControlFrameSettingsDropdown from './ControlFrameSettingsDropdown';
import KeyPoseGhostVisibilityToggle from './KeyPoseGhostVisibilityToggle';
import MotionDescriptionModal from './MotionDescriptionModal';
import MotionEditorHelpMenu from './MotionEditorHelpMenu';
import MotionEditorHotkeyInfo from './MotionEditorHotkeyInfo';

const ACTOR_DROPDOWN_ACCENT_CLASSES = {
  'bg-blue': {
    selectedText: 'text-blue',
    hover: 'hover:bg-blue hover:text-white dark:hover:bg-blue',
  },
  'bg-red': {
    selectedText: 'text-red',
    hover: 'hover:bg-red hover:text-white dark:hover:bg-red',
  },
  'bg-purple': {
    selectedText: 'text-purple',
    hover: 'hover:bg-purple hover:text-white dark:hover:bg-purple',
  },
  'bg-orange': {
    selectedText: 'text-orange',
    hover: 'hover:bg-orange hover:text-white dark:hover:bg-orange',
  },
} as const satisfies Record<
  (typeof ACTOR_ACCENT_BG_CLASSES)[number],
  { selectedText: string; hover: string }
>;

function Footer({
  hasMotionToPlay,
  showNameTagToggle,
  showReferencePicker = false,
  sceneReferences = [],
  selectedReferenceName = null,
  onSelectReference,
}: {
  hasMotionToPlay: boolean;
  showNameTagToggle: boolean;
  showReferencePicker?: boolean;
  sceneReferences?: MotionEditorActor[];
  selectedReferenceName?: string | null;
  onSelectReference?: (referenceName: string) => void;
}) {
  const {
    settings: {
      playback: { frameRate, playbackSpeed, loop },
      scene: { showActorNameTags },
    },
    setSettings,
  } = usePlayerSettings();
  const { activeActorId } = usePlayerAssetStatus();
  const activeReferenceName = sceneReferences.some(
    (reference) => reference.referenceName === activeActorId,
  )
    ? activeActorId
    : selectedReferenceName;
  const selectedReference = sceneReferences.find(
    (reference) => reference.referenceName === activeReferenceName,
  );
  const showReferenceSelector =
    showReferencePicker && sceneReferences.length > 1 && onSelectReference;
  const keyPoseStore = useKeyPoseStore();
  const motionEditorStore = useMotionEditorStore();
  const [motionDescriptionModalOpen, setMotionDescriptionModalOpen] =
    useState(false);
  const [hotkeyInfoOpen, setHotkeyInfoOpen] = useState(false);
  const hasMotionDescription = Boolean(
    motionEditorStore.state.motionDescription.trim(),
  );
  const motionDescriptionTooltip = useMemo(() => {
    const trimmed = motionEditorStore.state.motionDescription.trim();
    if (!trimmed) {
      return '';
    }

    return trimmed.length > 80 ? `${trimmed.slice(0, 80)}…` : trimmed;
  }, [motionEditorStore.state.motionDescription]);
  const showDebugStampEveryControls = useDebugStampEveryEnabled();
  const showControlFramesOnTimeline =
    keyPoseStore.state.controlFrameSettings.showControlFrames;

  return (
    <div className="flex flex-col">
      {hasMotionToPlay && (
        <div className="flex h-full flex-col justify-between px-2 pt-5 sm:px-0">
          <div className="flex min-w-0 gap-3">
            {showReferenceSelector ? (
              <ActorTimelineSelector
                actors={sceneReferences}
                disabled={!hasMotionToPlay}
                selectedActorLabel={selectedReference?.label ?? 'Select actor'}
                selectedReferenceName={activeReferenceName ?? null}
                onSelectReference={onSelectReference}
              />
            ) : null}
            <FooterTimelineControls
              showControlFramesOnTimeline={showControlFramesOnTimeline}
            />
          </div>
        </div>
      )}
      <div className="flex h-[64px] flex-none pr-2 lg:pr-0">
        <div className="flex flex-1 items-center justify-start">
          <div className="flex items-center gap-2 pl-2 lg:pl-0">
            <FooterFrameCounter
              frameRate={frameRate}
              hasMotionToPlay={hasMotionToPlay}
            />
            <Dropdown
              align="start"
              width={304}
              trigger={
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={!hasMotionToPlay}
                >
                  Playback: <span className="ml-1">{playbackSpeed}x</span>
                </Button>
              }
            >
              <div className="flex flex-col gap-2 px-2 py-4">
                <Text
                  variant="studio/body/medium/sm"
                  className="text-gray-60 dark:text-gray-45"
                >
                  Playback speed:
                </Text>
                <div className="flex w-full gap-2">
                  <LightweightSlider
                    max={2}
                    min={0.1}
                    step={0.1}
                    currentValue={playbackSpeed}
                    onChange={(value) => {
                      setSettings({
                        playback: {
                          playbackSpeed: value,
                        },
                      });
                    }}
                  />
                  <Text
                    as="div"
                    variant="studio/body/medium/sm"
                    className="bg-gray-15 dark:bg-gray-30 flex h-[26px] w-10 flex-none items-center justify-center rounded-lg px-2"
                  >
                    {playbackSpeed}x
                  </Text>
                </div>
              </div>
            </Dropdown>
            {ENABLE_MOTION_EDITOR_KEY_POSE_SURFACES ? (
              <Tooltip
                disabled={!hasMotionDescription}
                text={motionDescriptionTooltip}
              >
                <span className="inline-flex">
                  <Button
                    variant={hasMotionDescription ? 'primary' : 'secondary'}
                    size="sm"
                    disabled={!hasMotionToPlay}
                    onClick={() => setMotionDescriptionModalOpen(true)}
                  >
                    Motion description
                  </Button>
                </span>
              </Tooltip>
            ) : null}
            {ENABLE_MOTION_EDITOR_KEY_POSE_SURFACES &&
            showDebugStampEveryControls ? (
              <IntervalFramesQaControl hasMotionToPlay={hasMotionToPlay} />
            ) : null}
          </div>
        </div>
        <div className="flex h-full items-center gap-2">
          <FooterPlaybackButtons
            hasMotionToPlay={hasMotionToPlay}
            loop={loop}
          />
        </div>
        <div className="flex flex-1 items-center justify-end">
          <div className="flex items-center gap-2">
            {ENABLE_MOTION_EDITOR_KEY_POSE_SURFACES ? (
              <>
                <KeyPoseGhostVisibilityToggle
                  hasMotionToPlay={hasMotionToPlay}
                  sceneReferenceNames={sceneReferences.map(
                    (reference) => reference.referenceName,
                  )}
                />
                <ControlFrameSettingsDropdown
                  hasMotionToPlay={hasMotionToPlay}
                />
                <IntervalKeyPoseControls
                  hasMotionToPlay={hasMotionToPlay}
                  showDebugStampEveryControls={showDebugStampEveryControls}
                />
              </>
            ) : null}
            {showNameTagToggle ? (
              <CheckboxButton
                checked={showActorNameTags}
                checkboxPlacement="start"
                onCheckedChange={(checked) => {
                  setSettings({
                    scene: {
                      showActorNameTags: checked,
                    },
                  });
                }}
                disabled={!hasMotionToPlay}
              >
                Name tags
              </CheckboxButton>
            ) : null}
            <MotionEditorHelpMenu
              onOpenHotkeys={() => setHotkeyInfoOpen(true)}
            />
          </div>
        </div>
      </div>
      <MotionEditorHotkeyInfo
        open={hotkeyInfoOpen}
        onOpenChange={setHotkeyInfoOpen}
      />
      {ENABLE_MOTION_EDITOR_KEY_POSE_SURFACES ? (
        <MotionDescriptionModal
          open={motionDescriptionModalOpen}
          onOpenChange={setMotionDescriptionModalOpen}
        />
      ) : null}
    </div>
  );
}

export default memo(Footer);

function FooterTimelineControls({
  showControlFramesOnTimeline,
}: {
  showControlFramesOnTimeline: boolean;
}) {
  const actions = usePlayerActions();
  const {
    addKeyPoseAtCurrentFrame,
    addKeyPoseAtFrame,
    canPasteKeyPose,
    copyKeyPose,
    copySourceFrame,
    currentFrame,
    maxFrame,
    pasteKeyPoseAtFrame,
    removeKeyPose,
    setHoverPreviewFrame,
    timelineKeyPoses,
    toggleKeyPoseHidden,
  } = useKeyPoseCaptureActions();
  const visibleTimelineKeyPoses = useMemo(
    () =>
      showControlFramesOnTimeline
        ? timelineKeyPoses
        : timelineKeyPoses.filter((keyPose) => keyPose.stamp !== 'interval'),
    [showControlFramesOnTimeline, timelineKeyPoses],
  );

  useEffect(() => {
    return () => setHoverPreviewFrame(null);
  }, [setHoverPreviewFrame]);

  return (
    <div className="relative flex min-w-0 flex-1 flex-col">
      {ENABLE_MOTION_EDITOR_KEY_POSE_SURFACES ? (
        <KeyPoseTimeline
          maxFrame={maxFrame}
          canPasteKeyPose={canPasteKeyPose}
          keyPoses={visibleTimelineKeyPoses}
          onAddKeyPose={addKeyPoseAtFrame}
          onAddKeyPoseAtCurrentFrame={addKeyPoseAtCurrentFrame}
          onCopyFrame={copySourceFrame}
          onCopyKeyPose={copyKeyPose}
          onDeleteKeyPose={removeKeyPose}
          onEditKeyPose={actions.setFrame}
          onHoverKeyPoseFrameChange={setHoverPreviewFrame}
          onPasteKeyPose={pasteKeyPoseAtFrame}
          onSelectKeyPoseFrame={actions.setFrame}
          onToggleKeyPoseHidden={toggleKeyPoseHidden}
        />
      ) : null}
      <LightweightSlider
        min={0}
        max={maxFrame}
        currentValue={currentFrame}
        onChange={actions.setFrame}
      />
    </div>
  );
}

interface FooterFrameCounterProps {
  frameRate: number;
  hasMotionToPlay: boolean;
}

function FooterFrameCounter({
  frameRate,
  hasMotionToPlay,
}: FooterFrameCounterProps) {
  const timeline = usePlayerTimeline();
  const [showTime, setShowTime] = useState(false);
  const hasFrames = timeline.duration.frames > 0;
  const maxFrame = Math.max(timeline.duration.frames - 1, 0);
  const currentFrame = Math.min(Math.max(timeline.currentFrame, 0), maxFrame);
  const frameCountLabel = hasFrames
    ? `${currentFrame + 1}/${timeline.duration.frames}`
    : '0/0';
  const playbackLabel = useMemo(() => {
    if (!hasFrames) {
      return '0:00/0:00s';
    }

    if (showTime) {
      return `${formatTime(currentFrame / frameRate)}/${formatTime(
        timeline.duration.seconds,
      )}s`;
    }

    return frameCountLabel;
  }, [
    currentFrame,
    frameCountLabel,
    frameRate,
    hasFrames,
    showTime,
    timeline.duration.seconds,
  ]);

  if (!hasFrames) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={() => setShowTime((current) => !current)}
      className="hidden cursor-pointer md:inline-block"
      disabled={!hasMotionToPlay}
    >
      <Text
        as="div"
        variant="studio/body/medium/sm"
        className="text-gray-60 dark:text-gray-45 mr-2 -ml-2 min-w-[63px] text-end transition-all duration-100"
        style={{
          width:
            playbackLabel.split('/')[1]?.replace('.', '').toString().length *
              20 || 80,
        }}
      >
        {playbackLabel}
      </Text>
    </button>
  );
}

interface FooterPlaybackButtonsProps {
  hasMotionToPlay: boolean;
  loop: boolean;
}

function FooterPlaybackButtons({
  hasMotionToPlay,
  loop,
}: FooterPlaybackButtonsProps) {
  const actions = usePlayerActions();
  const playerStore = usePlayerStore();
  const playerStoreApi = usePlayerStoreApi();
  const { duration, isPlaying } = playerStore.state.transport;
  const hasFrames = duration.frames > 0;
  const maxFrame = Math.max(duration.frames - 1, 0);

  const handlePlayPause = () => {
    const currentFrame = playerStoreApi.state.transport.currentFrame;

    if (!isPlaying && !loop && hasFrames && currentFrame >= maxFrame) {
      actions.setFrame(0);
    }

    actions.playOrPause();
  };

  return (
    <>
      <IconOnlyFooterButton
        tooltipText="Previous frame"
        button={
          <Button
            variant="tertiary"
            icon={<ChevronLeftIcon className="pointer-events-none" />}
            onClick={() => actions.frameJump(-1)}
            disabled={!hasMotionToPlay}
          />
        }
      />
      <IconOnlyFooterButton
        tooltipText={isPlaying ? 'Pause' : 'Play'}
        button={
          <GoButton
            active={hasMotionToPlay}
            icon={
              isPlaying ? (
                <PauseIcon className="pointer-events-none" />
              ) : (
                <PlayIcon className="pointer-events-none" />
              )
            }
            onClick={handlePlayPause}
            disabled={!hasMotionToPlay}
          />
        }
      />
      <IconOnlyFooterButton
        tooltipText="Next frame"
        button={
          <Button
            variant="tertiary"
            icon={<ChevronRightIcon className="pointer-events-none" />}
            onClick={() => actions.frameJump(1)}
            disabled={!hasMotionToPlay}
          />
        }
      />
    </>
  );
}

function IntervalFramesQaControl({
  hasMotionToPlay,
}: {
  hasMotionToPlay: boolean;
}) {
  const keyPoseStore = useKeyPoseStore();
  const keyPoseStoreApi = useKeyPoseStoreApi();
  const { intervalFrames } = keyPoseStore.state.intervalAugmentationSettings;

  return (
    <Dropdown
      align="start"
      width={280}
      trigger={
        <Button variant="secondary" size="sm" disabled={!hasMotionToPlay}>
          Keys every N: <span className="ml-1">{intervalFrames}</span>
        </Button>
      }
    >
      <div className="flex flex-col gap-3 px-2 py-4">
        <IntervalAugmentationNumberField
          label="Interval frames (N)"
          value={intervalFrames}
          min={1}
          max={240}
          step={1}
          disabled={!hasMotionToPlay}
          onCommit={(value) => {
            keyPoseStoreApi.setIntervalAugmentationSettings({
              intervalFrames: value,
            });
          }}
        />
      </div>
    </Dropdown>
  );
}

function IntervalDeviationQaControls({
  hasMotionToPlay,
}: {
  hasMotionToPlay: boolean;
}) {
  const keyPoseStore = useKeyPoseStore();
  const keyPoseStoreApi = useKeyPoseStoreApi();
  const { subtleDeviationThreshold, deviationForMaxSpacing } =
    keyPoseStore.state.intervalAugmentationSettings;

  return (
    <div className="flex items-center gap-2">
      <IntervalAugmentationCompactNumberField
        label="Subtle"
        value={subtleDeviationThreshold}
        min={0}
        max={100}
        step={0.05}
        disabled={!hasMotionToPlay}
        onCommit={(value) => {
          keyPoseStoreApi.setIntervalAugmentationSettings({
            subtleDeviationThreshold: value,
          });
        }}
      />
      <IntervalAugmentationCompactNumberField
        label="Max dev"
        value={deviationForMaxSpacing}
        min={0.01}
        max={100}
        step={0.05}
        disabled={!hasMotionToPlay}
        onCommit={(value) => {
          keyPoseStoreApi.setIntervalAugmentationSettings({
            deviationForMaxSpacing: value,
          });
        }}
      />
    </div>
  );
}

function IntervalAugmentationCompactNumberField({
  label,
  value,
  min,
  max,
  step,
  disabled,
  onCommit,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  disabled?: boolean;
  onCommit: (value: number) => void;
}) {
  const [draftValue, setDraftValue] = useState(String(value));

  useEffect(() => {
    setDraftValue(String(value));
  }, [value]);

  const commitDraftValue = () => {
    const parsedValue = Number(draftValue);
    if (!Number.isFinite(parsedValue)) {
      setDraftValue(String(value));
      return;
    }

    onCommit(parsedValue);
  };

  return (
    <label className="flex items-center gap-1">
      <Text
        variant="studio/body/medium/sm"
        className="text-gray-60 dark:text-gray-45 whitespace-nowrap"
      >
        {label}
      </Text>
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        value={draftValue}
        onChange={(event) => setDraftValue(event.target.value)}
        onBlur={commitDraftValue}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            commitDraftValue();
          }
        }}
        className="bg-gray-15 hover:bg-gray-30 dark:bg-gray-75 dark:hover:bg-gray-60 focus-visible:bg-gray-30 dark:focus-visible:bg-gray-60 text-gray-90 h-7 w-16 rounded-lg px-2 text-[14px]/[18px] outline-hidden transition-all dark:text-white"
      />
    </label>
  );
}

function IntervalAugmentationNumberField({
  label,
  value,
  min,
  max,
  step,
  disabled,
  onCommit,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  disabled?: boolean;
  onCommit: (value: number) => void;
}) {
  const [draftValue, setDraftValue] = useState(String(value));

  useEffect(() => {
    setDraftValue(String(value));
  }, [value]);

  const commitDraftValue = () => {
    const parsedValue = Number(draftValue);
    if (!Number.isFinite(parsedValue)) {
      setDraftValue(String(value));
      return;
    }

    onCommit(parsedValue);
  };

  return (
    <label className="flex flex-col gap-1">
      <Text
        variant="studio/body/medium/sm"
        className="text-gray-60 dark:text-gray-45"
      >
        {label}
      </Text>
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        value={draftValue}
        onChange={(event) => setDraftValue(event.target.value)}
        onBlur={commitDraftValue}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            commitDraftValue();
          }
        }}
        className="bg-gray-15 hover:bg-gray-30 dark:bg-gray-75 dark:hover:bg-gray-60 focus-visible:bg-gray-30 dark:focus-visible:bg-gray-60 text-gray-90 h-8 w-full min-w-0 rounded-lg px-2 text-[14px]/[18px] outline-hidden transition-all dark:text-white"
      />
    </label>
  );
}

function IntervalKeyPoseControls({
  hasMotionToPlay,
  showDebugStampEveryControls,
}: {
  hasMotionToPlay: boolean;
  showDebugStampEveryControls: boolean;
}) {
  const keyPoseStore = useKeyPoseStore();
  const { intervalFrames } = keyPoseStore.state.intervalAugmentationSettings;
  const { addIntervalKeyPoses } = useKeyPoseCaptureActions();
  const addIntervalKeysTooltip = `Reconcile interval key poses every ${intervalFrames} frames`;

  return (
    <>
      {showDebugStampEveryControls ? (
        <IntervalDeviationQaControls hasMotionToPlay={hasMotionToPlay} />
      ) : null}
    </>
  );
}

interface IconOnlyFooterButtonProps {
  tooltipText: string;
  button: ReactNode;
}

function IconOnlyFooterButton({
  tooltipText,
  button,
}: IconOnlyFooterButtonProps) {
  return (
    <Tooltip text={tooltipText}>
      <span className="inline-flex">{button}</span>
    </Tooltip>
  );
}

function ActorTimelineSelector({
  actors,
  disabled,
  selectedReferenceName,
  selectedActorLabel,
  onSelectReference,
}: {
  actors: MotionEditorActor[];
  disabled: boolean;
  selectedReferenceName: string | null;
  selectedActorLabel: string;
  onSelectReference: (referenceName: string) => void;
}) {
  return (
    <div className={`flex-none`}>
      <Dropdown
        align="start"
        side="top"
        width={120}
        disabled={disabled}
        trigger={
          <button
            type="button"
            className="focus-visible hover:bg-gray-15 dark:hover:bg-gray-90/35 flex h-8 items-center justify-center gap-1 rounded-lg px-2 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            disabled={disabled}
          >
            <TextNew
              token="14/medium"
              className="text-primary max-w-[140px] truncate"
            >
              {selectedActorLabel}
            </TextNew>
            <ChevronDownIcon className="text-primary size-4 flex-none" />
          </button>
        }
      >
        <div className="flex flex-col">
          {actors.map((actor, index) => {
            const accent =
              ACTOR_ACCENT_BG_CLASSES[index % ACTOR_ACCENT_BG_CLASSES.length]!;
            const accentClasses = ACTOR_DROPDOWN_ACCENT_CLASSES[accent];

            return (
              <Dropdown.Item
                key={actor.referenceName}
                onClick={() => onSelectReference(actor.referenceName)}
                itemClassName={accentClasses.hover}
                textClassName={
                  actor.referenceName === selectedReferenceName
                    ? `${accentClasses.selectedText} group-hover:text-white`
                    : undefined
                }
              >
                {actor.label}
              </Dropdown.Item>
            );
          })}
        </div>
      </Dropdown>
    </div>
  );
}

const ChevronRightIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15.3208 10.0068C15.3208 10.1507 15.2931 10.2835 15.2378 10.4053C15.1825 10.527 15.0994 10.6432 14.9888 10.7539L8.52246 17.0791C8.33984 17.2673 8.11296 17.3613 7.8418 17.3613C7.66471 17.3613 7.50146 17.3171 7.35205 17.2285C7.20264 17.1455 7.08366 17.0293 6.99512 16.8799C6.91211 16.736 6.87061 16.5755 6.87061 16.3984C6.87061 16.1328 6.97021 15.8976 7.16943 15.6929L12.9966 10.0068L7.16943 4.3208C6.97021 4.11605 6.87061 3.88363 6.87061 3.62354C6.87061 3.44092 6.91211 3.27767 6.99512 3.13379C7.08366 2.98991 7.20264 2.87646 7.35205 2.79346C7.50146 2.70492 7.66471 2.66064 7.8418 2.66064C8.11296 2.66064 8.33984 2.75195 8.52246 2.93457L14.9888 9.25977C15.0994 9.37044 15.1825 9.48665 15.2378 9.6084C15.2931 9.73014 15.3208 9.86296 15.3208 10.0068Z"
        fill="currentColor"
      />
    </svg>
  );
};
